// EditKundenStandOrt.jsx
import React from "react";
import {
    Box,
    Paper,
    Grid,
    Typography,
    Button,
    IconButton,
    TextField,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    Stack,
    InputAdornment,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Add from "@mui/icons-material/Add";
import { useForm, FormProvider, Controller, useFieldArray, useWatch, useFormContext } from "react-hook-form";
import { ReactComponent as DeleteOutlineIcon } from "../../assets/DeleteSvg.svg";
import { ReactComponent as DeltaTechSvg } from "../../assets/DeltaTech.svg";
import { ReactComponent as BackSvg } from "../../assets/BackSvg.svg";

import GoBack from "../../components/GoBack";

const COUNTRIES = ["Deutschland", "Österreich", "Schweiz"];

// ---- builders ----
const makeDefaultBereich = () => ({ name: "" });
const makeDefaultLocation = () => ({
    name: "",
    sameAsInvoice: false,
    company: "",
    addressLine2: "",
    street: "",
    country: COUNTRIES[0],
    zip: "",
    city: "",
    email: "",
    bereiche: [],
});

export default function EditKundenStandOrt() {
    const methods = useForm({
        mode: "onChange",
        defaultValues: {
            customerName: "DELTA Tech",
            // invoice data (usually comes from Step 1; here dummy defaults)
            invoice: {
                company: "DELTA Tech GmbH",
                addressLine2: "Gebäude B",
                address: "Musterstraße 12",
                country: "Deutschland",
                zip: "70435",
                city: "Stuttgart",
                email: "rechnung@delta-tech.example",
            },
            // locations being edited
            locations: [
                {
                    ...makeDefaultLocation(),
                    name: "Werk Pforzheim",
                    company: "DELTA Tech GmbH",
                    street: "Industriestraße 22",
                    zip: "75172",
                    city: "Pforzheim",
                    email: "werk-pforzheim@example.com",
                    bereiche: [{ name: "Halle A" }, { name: "Halle B" }],
                },
            ],
        },
    });

    const {
        control,
        handleSubmit,
        getValues,
        setValue,
        formState: { isValid },
    } = methods;

    const { fields, append, remove } = useFieldArray({
        control,
        name: "locations",
    });

    const [expanded, setExpanded] = React.useState(fields.length ? 0 : -1);

    // ensure at least one location if none present
    React.useEffect(() => {
        if (fields.length === 0) {
            append(makeDefaultLocation());
            setExpanded(0);
        }
    }, []);

    const current = useWatch({ control, name: `locations.${expanded}` });

    // validation gate for "Standort hinzufügen"
    const isCurrentValid = React.useMemo(() => {
        if (!current) return false;
        const { name, sameAsInvoice, bereiche = [] } = current;
        if (!name?.trim()) return false;

        if (!sameAsInvoice) {
            const req = ["company", "street", "country", "zip", "city", "email"];
            for (const k of req) {
                const v = (current[k] ?? "").toString().trim();
                if (!v) return false;
            }
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(current.email)) return false;
        }

        if (bereiche.length > 0 && !bereiche.every((b) => (b.name ?? "").trim())) return false;

        return true;
    }, [current]);

    // invoice -> location copy
    const copyFromInvoice = (idx) => {
        const inv = getValues("invoice") || {};
        const patch = {
            company: inv.company || "",
            addressLine2: inv.addressLine2 || "",
            street: inv.address || inv.street || "",
            country: inv.country || COUNTRIES[0],
            zip: inv.zip || "",
            city: inv.city || "",
            email: inv.email || "",
        };
        Object.entries(patch).forEach(([k, v]) => {
            setValue(`locations.${idx}.${k}`, v, {
                shouldDirty: true,
                shouldValidate: true,
            });
        });
    };

    const handleAddLocation = () => {
        const next = fields.length;
        append(makeDefaultLocation());
        setExpanded(next);
    };

    const onSubmit = (data) => {
        // TODO: wire up to API
        // eslint-disable-next-line no-console
        console.log("Save Standorte payload:", data);
    };

    return (
        <Box sx={{ p: 2 }} className="editKundenStandOrtPage">

            <GoBack icon={<BackSvg />} text={"Kunde DELTA Tech"} color="rgba(0, 0, 0, 0.50)" to={"/kunden-detail"} />
            <br />
            <br />

            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, borderColor: "#E6E8EB" }}>
                <Box sx={{ mb: 2 }}>
                    <Typography className="textSecondary" sx={{ fontWeight: 500, mb: 2 }}>
                        <DeltaTechSvg /> {getValues("customerName") || "Company Name"}
                    </Typography>
                    <Typography variant="h6"  className="fRubik"  sx={{ fontWeight: 500, color: "var(--color-primary)" }}>
                        Standorte verwalten
                    </Typography>
                    <Typography className="textSecondary" sx={{ fontWeight: 500 }}>
                        Ein Kunde kann beliebig viele Standorte haben.
                    </Typography>
                </Box>

                <FormProvider {...methods}>
                    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
                        {fields.map((field, idx) => (
                            <Accordion
                                key={field.id}
                                expanded={expanded === idx}
                                onChange={(_, open) => setExpanded(open ? idx : -1)}
                                disableGutters
                                sx={{
                                    mb: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 2,
                                    "&:before": { display: "none" },
                                }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{ width: "100%" }}
                                    >
                                        <Typography sx={{ fontWeight: 500, color: "var(--color-primary)" }}>
                                            {getValues(`locations.${idx}.name`)?.trim() || "Neuer Standort"}
                                        </Typography>

                                        <IconButton
                                            aria-label="Entfernen"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                remove(idx);
                                                if (fields.length - 1 <= 1) setExpanded(0);
                                                else setExpanded((prev) => Math.max(0, prev - 1));
                                            }}
                                        >
                                            <DeleteOutlineIcon color="error" />
                                        </IconButton>
                                    </Stack>
                                </AccordionSummary>

                                <AccordionDetails sx={{ paddingRight: "20%" }}>
                                    {/* Name + checkbox */}
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
                                            <Controller
                                                name={`locations.${idx}.name`}
                                                control={control}
                                                rules={{ required: "Pflichtfeld" }}
                                                render={({ field, fieldState }) => (
                                                    <TextField
                                                        {...field}
                                                        fullWidth
                                                        label="Standortname"
                                                        placeholder='z. B.: "Werk Neckarsulm"'
                                                        error={!!fieldState.error}
                                                        helperText={fieldState.error?.message || 'z. B.: "Werk Neckarsulm"'}
                                                    />
                                                )}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
                                            <Controller
                                                name={`locations.${idx}.sameAsInvoice`}
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                {...field}
                                                                checked={!!field.value}
                                                                onChange={(_, checked) => {
                                                                    field.onChange(checked);
                                                                    if (checked) copyFromInvoice(idx);
                                                                }}
                                                            />
                                                        }
                                                        label="Anschrift wie Rechnungsadresse"
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* Address block (hidden if sameAsInvoice) */}
                                    <AddressSection idx={idx} />

                                    <Divider sx={{ my: 2 }} />

                                    {/* Bereiche block */}
                                    <BereicheSection idx={idx} />
                                </AccordionDetails>
                            </Accordion>
                        ))}

                        <Button
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={handleAddLocation}
                            disabled={fields.length > 0 && !isCurrentValid}
                            sx={{ mt: 1 }}
                        >
                            Standort hinzufügen
                        </Button>

                        <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={!isValid}
                                sx={{ bgcolor: "var(--color-primary)", "&:hover": { bgcolor: "#0b3140" } }}
                            >
                                ÄNDERUNGEN SPEICHERN
                            </Button>
                        </Stack>
                    </Box>
                </FormProvider>
            </Paper>
        </Box>
    );
}

/* ---------- Address section (hidden if sameAsInvoice) ---------- */
function AddressSection({ idx }) {
    const { control } = useFormContext();
    const sameAsInvoice = useWatch({ control, name: `locations.${idx}.sameAsInvoice` });

    if (sameAsInvoice) return null;

    return (
        <Grid container spacing={4} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
                <Controller
                    name={`locations.${idx}.company`}
                    control={control}
                    rules={{ required: "Pflichtfeld" }}
                    render={({ field, fieldState }) => (
                        <TextField {...field} fullWidth label="Unternehmen" error={!!fieldState.error} helperText={fieldState.error?.message} />
                    )}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <Controller
                    name={`locations.${idx}.addressLine2`}
                    control={control}
                    render={({ field }) => <TextField {...field} fullWidth label="Adresszusatz (optional)" />}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <Controller
                    name={`locations.${idx}.street`}
                    control={control}
                    rules={{ required: "Pflichtfeld" }}
                    render={({ field, fieldState }) => (
                        <TextField {...field} fullWidth label="Straße und Hausnummer" error={!!fieldState.error} helperText={fieldState.error?.message} />
                    )}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <Controller
                    name={`locations.${idx}.country`}
                    control={control}
                    rules={{ required: "Pflichtfeld" }}
                    render={({ field, fieldState }) => (
                        <TextField {...field} select fullWidth label="Land" error={!!fieldState.error} helperText={fieldState.error?.message}>
                            {COUNTRIES.map((c) => (
                                <MenuItem key={c} value={c}>
                                    {c}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                />
            </Grid>

            <Grid item xs={12} md={3}>
                <Controller
                    name={`locations.${idx}.zip`}
                    control={control}
                    rules={{
                        required: "Pflichtfeld",
                        pattern: { value: /^[0-9A-Za-z -]{3,10}$/, message: "Ungültige PLZ" },
                    }}
                    render={({ field, fieldState }) => (
                        <TextField {...field} fullWidth label="PLZ" error={!!fieldState.error} helperText={fieldState.error?.message} />
                    )}
                />
            </Grid>

            <Grid item xs={12} md={3}>
                <Controller
                    name={`locations.${idx}.city`}
                    control={control}
                    rules={{ required: "Pflichtfeld" }}
                    render={({ field, fieldState }) => (
                        <TextField {...field} fullWidth label="Ort" error={!!fieldState.error} helperText={fieldState.error?.message} />
                    )}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <Controller
                    name={`locations.${idx}.email`}
                    control={control}
                    rules={{
                        required: "Pflichtfeld",
                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Ungültige E-Mail-Adresse" },
                    }}
                    render={({ field, fieldState }) => (
                        <TextField {...field} fullWidth label="E-Mail-Adresse" error={!!fieldState.error} helperText={fieldState.error?.message} />
                    )}
                />
            </Grid>
        </Grid>
    );
}

/* --------------------------- Bereiche (optional, 2 per row) --------------------------- */
function BereicheSection({ idx }) {
    const { control } = useFormContext();
    const { fields, append, remove } = useFieldArray({
        control,
        name: `locations.${idx}.bereiche`,
    });

    return (
        <Box>
            <Typography sx={{ fontWeight: 500, mb: 0.5, mt: 2, color: "var(--color-primary)" }}>
                Bereiche
            </Typography>
            <Typography className="textSecondary" sx={{ mb: 3 }}>
                Bereiche sind optional. Wenn du welche hinzufügst, benenne sie bitte.
            </Typography>

            <Grid container spacing={4}>
                {fields.map((field, bIndex) => (
                    <Grid item xs={12} md={6} key={field.id}>
                        <Controller
                            name={`locations.${idx}.bereiche.${bIndex}.name`}
                            control={control}
                            rules={{ required: "Pflichtfeld" }}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    label="Bereichsname"
                                    placeholder='z. B.: "Metallfertigung"'
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message || 'z. B.: "Metallfertigung"'}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton aria-label="Bereich entfernen" onClick={() => remove(bIndex)} edge="end">
                                                    <DeleteOutlineIcon color="error" />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>
                ))}
            </Grid>

            <Button size="small" variant="text" startIcon={<Add />} onClick={() => append(makeDefaultBereich())} sx={{ mt: 1 }}>
                Bereich hinzufügen
            </Button>
        </Box>
    );
}
